import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'
import { ROUTES } from '@/constants'
import { PageLoader } from '@/components/ui/PageLoader'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { PermissionRoute } from '@/components/auth/PermissionRoute'
import { PERMISSIONS, type PermissionKey } from '@/services/permissions'
import LandingRedirect from '@/components/auth/LandingRedirect'
import { PublicRoute } from '@/components/auth/PublicRoute'
import { RootLayout } from '@/components/layout/RootLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PatientPortalLayout } from '@/components/layout/PatientPortalLayout'
import { ErrorPage } from '@/components/ui/ErrorPage'

// ── Auth ──────────────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const OtpVerifyPage = lazy(() => import('@/pages/auth/OtpVerifyPage'))
const SetPasswordPage = lazy(() => import('@/pages/auth/SetPasswordPage'))

// ── Clinician/Admin Portal ────────────────────────────────────────────────────
const ClinicianDashboard = lazy(() => import('@/pages/clinician-portal/dashboard'))
const ClinicianAppointments = lazy(
  () => import('@/pages/clinician-portal/appointments/appointment-index'),
)
const ClinicianAppointmentsAvailability = lazy(
  () => import('@/pages/clinician-portal/appointments/availability'),
)
const ClinicianEncounters = lazy(
  () => import('@/pages/clinician-portal/encounters/encounter-tab-index'),
)
const ClinicianStartEncounter = lazy(
  () => import('@/pages/clinician-portal/encounters/start-encounter-form'),
)
const ClinicianTasks = lazy(() => import('@/pages/clinician-portal/task/clinician-task-page-index'))
const ClinicianPatients = lazy(() => import('@/pages/clinician-portal/patients'))
const ClinicianPatientChart = lazy(
  () => import('@/pages/clinician-portal/patient-chart/patient-chart-index'),
)
const ClinicianSubscriptions = lazy(() => import('@/pages/clinician-portal/subscriptions'))
const ClinicianCommunications = lazy(() => import('@/pages/clinician-portal/communications'))
const ClinicianLabOrders = lazy(() => import('@/pages/clinician-portal/lab-orders'))
const ClinicianLabOrdersOauthCallback = lazy(
  () => import('@/pages/clinician-portal/lab-orders/oauth-callback'),
)
const ClinicianPrescriptions = lazy(() => import('@/pages/clinician-portal/prescriptions'))
const ClinicianInventory = lazy(() => import('@/pages/clinician-portal/inventory'))
const ClinicianSuperbills = lazy(() => import('@/pages/clinician-portal/superbills'))
const ClinicianReports = lazy(() => import('@/pages/clinician-portal/reports'))
// Advocate worklist — commercial plans assigned to the signed-in advocate.
const ClinicianPlanOfCare = lazy(
  () => import('@/pages/clinician-portal/plan-of-care/advocate-plans-page'),
)
const ClinicianPlanOfCareDetail = lazy(
  () => import('@/pages/clinician-portal/plan-of-care/advocate-plan-detail-page'),
)
const ClinicianSettings = lazy(() => import('@/pages/clinician-portal/settings'))
const ClinicianProfile = lazy(() => import('@/pages/clinician-portal/profile'))
const ClinicianAffiliates = lazy(() => import('@/pages/clinician-portal/affiliates'))
const AdminClinics = lazy(() => import('@/pages/admin/clinics'))
const ClinicDetailPage = lazy(() => import('@/pages/admin/clinics/components/ClinicDetailPage'))
const AdminUsers = lazy(() => import('@/pages/admin/users/users-list'))
const UserDetailPage = lazy(() => import('@/pages/admin/users/components/UserDetailPage'))

// ── Patient Portal ────────────────────────────────────────────────────────────
const PatientDashboard = lazy(() => import('@/pages/patient-portal/dashboard'))
const PatientAppointments = lazy(
  () => import('@/pages/patient-portal/appointments/patient-appointment-page'),
)
const PatientDocuments = lazy(
  () => import('@/pages/patient-portal/documents/patient-document-page-index'),
)
const PatientVirtualCalls = lazy(() => import('@/pages/patient-portal/virtual-calls'))
const PatientSubscriptions = lazy(() => import('@/pages/patient-portal/subscriptions'))
const PatientPlanOfCare = lazy(() => import('@/pages/patient-portal/plan-of-care'))
const PatientPlanOfCareDetail = lazy(() => import('@/pages/patient-portal/plan-of-care/detail'))
const PatientLabReports = lazy(() => import('@/pages/patient-portal/lab-reports'))
const PatientRecommendations = lazy(() => import('@/pages/patient-portal/recommendations'))
const PatientSupplements = lazy(() => import('@/pages/patient-portal/supplements'))
const PatientPurchases = lazy(() => import('@/pages/patient-portal/purchases'))
const PatientSuperbills = lazy(() => import('@/pages/patient-portal/superbills'))
const PatientPaymentMethods = lazy(() => import('@/pages/patient-portal/payment-methods'))
const PatientSettings = lazy(() => import('@/pages/patient-portal/settings'))
const PatientProfile = lazy(() => import('@/pages/patient-portal/profile'))

// ── Telehealth (shared route — accessible from both portals) ────────────────
// Renders ZoomCallOverlay (iframe → ZoomSession?embed=1) so a call opened via
// a shared link shows the SAME overlay UI as the in-app "Join Video Call"
// button. The iframe itself loads /telehealth/{room}?embed=1 → ZoomSession.
const TelehealthRoute = lazy(() => import('@/integrations/zoom/TelehealthRoute'))

// eslint-disable-next-line react-refresh/only-export-components
const S = (Component: React.ComponentType) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

/** A lazy page behind a permission; the Forbidden page renders inside the layout otherwise. */
// eslint-disable-next-line react-refresh/only-export-components
const P = (Component: React.ComponentType, permission: PermissionKey | PermissionKey[]) => (
  <PermissionRoute permission={permission}>{S(Component)}</PermissionRoute>
)

const ClientChartRedirect = () => {
  const { id = '' } = useParams<{ id: string }>()
  return <Navigate to={ROUTES.APP.CLIENTS_DETAIL_TAB(id, 'appointments')} replace />
}

/**
 * Router — single codebase, portal detected from URL/domain.
 *
 * /auth/*     → shared login (portal auto-detected)
 * /app/*      → clinician/admin portal (DashboardLayout + sidebar)
 * /patient/*  → patient portal (PatientPortalLayout + top nav)
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      // ── AUTH (shared by all portals) ────────────────────────────────────
      {
        path: 'auth',
        element: (
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        ),
        children: [
          { path: 'login', element: S(LoginPage) },
          { path: 'forgot-password', element: S(ForgotPasswordPage) },
          { path: 'verify-otp/:email', element: S(OtpVerifyPage) },
          { path: 'reset-password/:email', element: S(OtpVerifyPage) },
          { path: 'set-password/:otp/reset/:username', element: S(SetPasswordPage) },
          { index: true, element: <Navigate to="login" replace /> },
        ],
      },

      // ── CLINICIAN/ADMIN PORTAL ──────────────────────────────────────────
      {
        path: 'app',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'clinician']}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <LandingRedirect /> },
          {
            path: 'dashboard',
            element: P(ClinicianDashboard, PERMISSIONS.DASHBOARD_DASHBOARD_VIEW),
          },
          {
            path: 'appointments',
            element: P(ClinicianAppointments, PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW),
          },
          {
            path: 'appointments/availability',
            element: P(
              ClinicianAppointmentsAvailability,
              PERMISSIONS.AVAILABILITY_AVAILABILITY_VIEW,
            ),
          },
          { path: 'clients', element: P(ClinicianPatients, PERMISSIONS.PATIENTS_PATIENT_VIEW) },
          {
            path: 'clients/:id',
            element: (
              <PermissionRoute permission={PERMISSIONS.PATIENTS_PATIENT_OPEN_CHART}>
                <ClientChartRedirect />
              </PermissionRoute>
            ),
          },
          {
            path: 'clients/:id/:tab',
            element: P(ClinicianPatientChart, PERMISSIONS.PATIENTS_PATIENT_OPEN_CHART),
          },
          {
            path: 'encounters',
            element: P(ClinicianEncounters, PERMISSIONS.ENCOUNTERS_ENCOUNTER_VIEW),
          },
          {
            path: 'encounters/:encounterUuid',
            element: P(ClinicianStartEncounter, PERMISSIONS.ENCOUNTERS_ENCOUNTER_VIEW),
          },
          { path: 'tasks', element: P(ClinicianTasks, PERMISSIONS.TASKS_TASK_VIEW) },
          {
            path: 'subscriptions',
            element: P(ClinicianSubscriptions, PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW),
          },
          {
            path: 'communications',
            element: P(ClinicianCommunications, PERMISSIONS.COMMUNICATION_CALL_LOGS_VIEW),
          },
          { path: 'lab-orders', element: S(ClinicianLabOrders) },
          {
            path: 'lab-orders/oauth/callback',
            element: S(ClinicianLabOrdersOauthCallback),
          },
          { path: 'prescriptions', element: S(ClinicianPrescriptions) },
          {
            path: 'inventory',
            element: P(ClinicianInventory, PERMISSIONS.INVENTORY_INVENTORY_VIEW),
          },
          { path: 'superbills', element: S(ClinicianSuperbills) },
          { path: 'reports', element: P(ClinicianReports, PERMISSIONS.REPORTS_FINANCIAL_VIEW) },
          {
            path: 'plan-of-care',
            element: P(ClinicianPlanOfCare, PERMISSIONS.COMMERCIAL_PLAN_PLAN_ADVOCATE),
          },
          {
            path: 'plan-of-care/:commercialPlanUuid',
            // Reached from the advocate worklist and from the chart's Commercial Plan item alike.
            element: P(ClinicianPlanOfCareDetail, [
              PERMISSIONS.COMMERCIAL_PLAN_PLAN_VIEW_CLINICAL,
              PERMISSIONS.COMMERCIAL_PLAN_PLAN_VIEW_CLIENT,
              PERMISSIONS.COMMERCIAL_PLAN_PLAN_ADVOCATE,
            ]),
          },
          {
            path: 'settings',
            children: [
              { index: true, element: <Navigate to="intake-forms" /> },
              // Charge List has Active/Archived sub-tabs — default to Active.
              { path: 'charge-list', element: <Navigate to="active" /> },
              { path: ':tab', element: S(ClinicianSettings) },
              { path: ':tab/:subTab', element: S(ClinicianSettings) },
            ],
          },
          { path: 'profile', element: S(ClinicianProfile) },
          { path: 'affiliates', element: S(ClinicianAffiliates) },
          { path: 'clinics', element: P(AdminClinics, PERMISSIONS.CLINIC_CLINIC_VIEW) },
          { path: 'clinics/:id', element: P(ClinicDetailPage, PERMISSIONS.CLINIC_CLINIC_VIEW) },
          { path: 'users', element: P(AdminUsers, PERMISSIONS.USERS_USER_VIEW) },
          { path: 'users/:id', element: P(UserDetailPage, PERMISSIONS.USERS_USER_VIEW) },
        ],
      },

      // ── PATIENT PORTAL ──────────────────────────────────────────────────
      {
        path: 'patient',
        element: (
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientPortalLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: S(PatientDashboard) },
          { path: 'appointments', element: S(PatientAppointments) },
          { path: 'documents', element: S(PatientDocuments) },
          { path: 'virtual-calls', element: S(PatientVirtualCalls) },
          { path: 'subscriptions', element: S(PatientSubscriptions) },
          { path: 'plan-of-care', element: S(PatientPlanOfCare) },
          { path: 'plan-of-care/:commercialPlanUuid', element: S(PatientPlanOfCareDetail) },
          { path: 'lab-reports', element: S(PatientLabReports) },
          { path: 'recommendations', element: S(PatientRecommendations) },
          { path: 'supplements', element: S(PatientSupplements) },
          { path: 'purchases', element: S(PatientPurchases) },
          { path: 'superbills', element: S(PatientSuperbills) },
          { path: 'payment-methods', element: S(PatientPaymentMethods) },
          { path: 'settings', element: S(PatientSettings) },
          { path: 'profile', element: S(PatientProfile) },
        ],
      },

      // ── TELEHEALTH (shared by clinician + patient portals) ──────────────
      // Path mirrors the BE-issued join URLs (patientJoinUrl / providerJoinUrl)
      // so the same component services both roles.
      {
        path: 'telehealth/:room',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'clinician', 'patient']}>
            {S(TelehealthRoute)}
          </ProtectedRoute>
        ),
      },

      // ── DEFAULT REDIRECT ────────────────────────────────────────────────
      { index: true, element: <Navigate to="/auth/login" replace /> },
    ],
  },
])
